import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Message, Select, Spin, Tag, Typography } from "@arco-design/web-react";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard, { SummaryCardGroup } from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi } from "../Api/AuthenticatedApi";
import { valueToPercent } from "../Broadcast/broadcastStats";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import SequenceSection from "./SequenceSection";
import SpySection from "./SpySection";
import {
  DEVICE_KINDS, NO_DEVICE_METRICS, conversionRate, deviceQuery, devicesFromParams,
  rangeFromParams, rangeLabel, rangePresets, reportParams, shortDay, viewerTimeZone,
} from "./reportRange";

const { Text } = Typography;

const EMPTY = {
  contacts: { total: 0, active: 0, bounced: 0, complained: 0, unsubscribed: 0 },
  broadcasts: { sent: 0, opened: 0, bounces: 0, complaints: 0, open_rate: 0, bounce_rate: 0, complaint_rate: 0 },
};

export default function Reports() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // The filters are kept in the address, so coming back to this page finds them
  // as they were. Signup activity covers the chosen days (from, to: YYYY-MM-DD,
  // local) and the chosen device kinds; none picked is all devices.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const devicesParam = searchParams.get("devices");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const range = useMemo(() => rangeFromParams(searchParams), [fromParam, toParam]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const devices = useMemo(() => devicesFromParams(searchParams), [devicesParam]);
  const setFilters = (nextRange, nextDevices) =>
    setSearchParams(reportParams(nextRange, nextDevices), { replace: true });
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    FetchApi(
      getAccessTokenSilently,
      "/api/reports/overview",
      (d) => { setData(d || EMPTY); setLoading(false); },
      () => navigate("/login"),
      () => { setLoading(false); Message.error("Failed to load reports"); }
    );
  }, [getAccessTokenSilently, navigate]);

  useEffect(() => {
    setActivityLoading(true);
    const q = new URLSearchParams({ from: range[0], to: range[1], tz: viewerTimeZone() });
    const kinds = deviceQuery(devices);
    if (kinds) q.set("devices", kinds);
    FetchApi(
      getAccessTokenSilently,
      "/api/reports/activity?" + q.toString(),
      (d) => { setActivity(d); setActivityLoading(false); },
      () => navigate("/login"),
      () => { setActivityLoading(false); Message.error("Failed to load signup activity"); }
    );
  }, [range, devices, getAccessTokenSilently, navigate]);

  if (loading) {
    return <LoadingState />;
  }

  const b = data.broadcasts || EMPTY.broadcasts;
  const c = data.contacts || EMPTY.contacts;
  const n = (x) => (x || 0).toLocaleString();
  const t = (activity && activity.totals) || {};
  const days = (activity && activity.days) || [];
  // Filtered by device, the numbers with no device don't apply.
  const filtered = deviceQuery(devices) !== "";
  const m = (key) => (filtered && NO_DEVICE_METRICS.includes(key) ? "—" : n(t[key]));
  const hasGrowth = days.some((d) => (!filtered && d.new_contacts) || d.form_submissions || d.confirmed);
  const label = rangeLabel(range) + (filtered
    ? " · " + DEVICE_KINDS.filter((c) => devices.includes(c.key)).map((c) => c.label).join(", ")
    : "");

  return (
    <Content>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <SimpleBreadcrumbs elements={[{ name: "Reports" }]} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Select
          aria-label="Devices"
          mode="multiple"
          style={{ minWidth: 200, maxWidth: 360 }}
          placeholder="All devices"
          allowClear
          value={devices}
          onChange={(next) => setFilters(range, next || [])}
          options={DEVICE_KINDS.map((c) => ({ label: c.label, value: c.key }))}
        />
        <DatePicker.RangePicker
          aria-label="Date range"
          style={{ width: 280 }}
          allowClear={false}
          format="YYYY-MM-DD"
          value={range}
          disabledDate={(d) => d.isAfter(dayjs(), "day")}
          shortcutsPlacementLeft
          shortcuts={rangePresets().map((p) => ({ text: p.text, value: () => p.value.map((v) => dayjs(v)) }))}
          onChange={(dates) => { if (dates && dates[0] && dates[1]) setFilters([dates[0], dates[1]], devices); }}
        />
        </div>
      </div>

      <Spin loading={activityLoading} block>
        <SummaryCard title="Signup activity" subTitle={label}>
          <SummaryCardGroup>
            <SummaryCard title="Form views" value={n(t.form_views)} />
            <SummaryCard title="Form submissions" value={n(t.form_submissions)} />
            <SummaryCard title="Conversion rate" value={conversionRate(t.form_submissions, t.form_views)} />
            <SummaryCard title="New contacts" value={m("new_contacts")} />
            <SummaryCard title="Signup emails sent" value={m("signup_emails")} />
            <SummaryCard title="Emails confirmed" value={n(t.confirmed)} />
            <SummaryCard title="Incentives sent" value={m("incentives_sent")} />
            <SummaryCard title="Incentives downloaded" value={n(t.incentives_downloaded)} />
          </SummaryCardGroup>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            Form submissions include people signing up again. Signup emails include re-sends and reward follow-ups.
            {filtered
              ? " With a device filter, only what's counted by device is shown, starting October 2026. New contacts, signup emails and incentives sent happen on our server, not on a device, so they show —."
              : " Form views from before daily counting began (October 2026) aren't included."}
          </Text>
        </SummaryCard>

        <SummaryCard title="List growth" subTitle={label}>
          {!hasGrowth ? (
            <Text type="secondary">No signups in this date range.</Text>
          ) : (
            <ResponsiveContainer width="100%" height={280} debounce={150}>
              <LineChart data={days} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={shortDay} minTickGap={24} />
                <YAxis allowDecimals={false} />
                {/* No animation: an animated tooltip slides after the mouse and shakes. */}
                <Tooltip isAnimationActive={false} labelFormatter={shortDay} />
                <Legend />
                {!filtered && <Line type="monotone" dataKey="new_contacts" name="New contacts" stroke="#165dff" dot={days.length <= 45} isAnimationActive={false} />}
                <Line type="monotone" dataKey="form_submissions" name="Form submissions" stroke="#14c9c9" dot={days.length <= 45} isAnimationActive={false} />
                <Line type="monotone" dataKey="confirmed" name="Emails confirmed" stroke="#f7ba1e" dot={days.length <= 45} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </SummaryCard>

        <SummaryCard title="Email opens and clicks" subTitle={label}>
          <SummaryCardGroup>
            <SummaryCard title="Email opens" value={n(t.email_opens)} />
            <SummaryCard title="Email clicks" value={n(t.email_clicks)} />
          </SummaryCardGroup>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            Every email: signup, broadcast and sequence. Opening the same email twice counts twice. Clicks are tracked links in signup emails.
            Gmail, Yahoo and Apple Mail load images on their own servers, so most opens show as Unknown device. Counted since October 2026.
          </Text>
        </SummaryCard>
      </Spin>

      <SummaryCard title="Broadcast deliverability" subTitle="All time, across all your broadcasts">
        <SummaryCardGroup>
          <SummaryCard title="Sent" value={n(b.sent)} />
          <SummaryCard title="Opens" value={n(b.opened)} />
          <SummaryCard title="Open rate" value={valueToPercent(b.open_rate)} />
          <SummaryCard title="Bounces" value={n(b.bounces)} />
          <SummaryCard title="Bounce rate" value={valueToPercent(b.bounce_rate)} />
          <SummaryCard title="Complaints" value={n(b.complaints)} />
          <SummaryCard title="Complaint rate" value={valueToPercent(b.complaint_rate)} />
        </SummaryCardGroup>
      </SummaryCard>

      <SequenceSection range={range} />

      <SummaryCard title="Audience" subTitle="Why contacts fell out of sending">
        <SummaryCardGroup>
          <SummaryCard title="Total contacts" value={n(c.total)} />
          <SummaryCard title="Active" value={n(c.active)} />
        </SummaryCardGroup>
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Text type="secondary" style={{ marginRight: 4 }}>Suppressed:</Text>
          <Tag color="red">{n(c.bounced)} bounced</Tag>
          <Tag color="orangered">{n(c.complained)} complained</Tag>
          <Tag color="gray">{n(c.unsubscribed)} unsubscribed</Tag>
        </div>
      </SummaryCard>

      <SpySection range={range} />
    </Content>
  );
}
